import type { Side } from '../shared/espn'

/*
 * The offline queue.
 *
 * Measured against the live deployment, a tap cost 300-490ms before anything
 * appeared: a POST, then a full refetch of the entire state to learn one fact
 * the client had just decided. On a phone on bad wifi that is seconds, or the
 * tap is lost outright - which is what "clunky, not registering" is.
 *
 * So the network leaves the path of a tap entirely. A pick lands in local
 * state, is written to storage, and drains in the background. Nothing on
 * screen ever waits for a response, and a pick made in a dead spot is still
 * there when the phone finds signal again.
 */

export interface Pending {
  gameId: string
  playerId: string
  side: Side
  /** When it was tapped, so the newest answer wins a race. */
  at: number
}

/** The two methods of `localStorage` this needs, so it can be tested without one. */
export interface Store {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

const KEY = 'slate.queue'

/**
 * Add a pick, replacing any pending pick for the same game by the same player.
 *
 * Tapping away and then home before the queue drains must send ONE pick and it
 * must be the second - queueing both is a race that the wrong answer can win.
 * The replacement keeps its original position so the board drains in the order
 * it was filled in.
 */
export function enqueue(queue: readonly Pending[], p: Pending): Pending[] {
  const i = queue.findIndex((q) => q.gameId === p.gameId && q.playerId === p.playerId)
  if (i === -1) return [...queue, p]
  const next = [...queue]
  next[i] = p
  return next
}

/**
 * Take one item out, but ONLY if it is still the item that was sent.
 *
 * This is the fix for the lost-update race. The drain used to write back its
 * own stale snapshot minus the first entry, which threw away any tap that
 * landed while the post was in flight - so switching picks quickly made one
 * vanish a moment after it appeared, which reads as the app undoing you.
 *
 * Matching on `at` is what makes it safe. If the player changed their mind on
 * the same game while the first request was running, the queue now holds a
 * NEWER pick with a later timestamp, and the old one coming back successful
 * must not delete it - otherwise the screen shows one answer and the server
 * holds the other, permanently.
 */
export function removeSynced(queue: readonly Pending[], sent: Pending): Pending[] {
  return queue.filter(
    (q) => !(q.gameId === sent.gameId && q.playerId === sent.playerId && q.at === sent.at),
  )
}

export function loadQueue(store: Store): Pending[] {
  /*
   * An unreadable queue is an empty one. Private mode, a cleared site and a
   * half-written value all land here, and none of them may take the app down.
   */
  try {
    const raw = store.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Pending[]) : []
  } catch {
    return []
  }
}

export function saveQueue(store: Store, queue: readonly Pending[]): void {
  try {
    store.setItem(KEY, JSON.stringify(queue))
  } catch {
    /* Storage refused. The queue still lives in memory for this session. */
  }
}

/**
 * What the board should show: the server's answer, overridden by anything
 * still pending for this player.
 *
 * The override is the point. A refetch that lands between a tap and its sync
 * would otherwise flip the row back to the old answer under the player's
 * thumb, which reads as the app undoing their pick.
 */
export function mergePicks(
  serverPicks: readonly { gameId: string; playerId: string; side: Side }[],
  queue: readonly Pending[],
  playerId: string,
  sent: readonly Pending[] = [],
): Map<string, Side> {
  const out = new Map<string, Side>()
  for (const p of serverPicks) if (p.playerId === playerId) out.set(p.gameId, p.side)
  /*
   * `sent` sits between the server and the queue, and it is what stops the
   * flash.
   *
   * A pick leaves the queue the moment its POST succeeds - but the refetched
   * state has not arrived yet, so for one render the only answer left is the
   * server's, which is still the OLD pick. The row showed the new team, blinked
   * back to the old one, then settled on the new one again.
   *
   * Holding it here until the server itself agrees closes that window. The
   * queue still sits on top, so changing your mind again mid-flight wins.
   */
  for (const p of sent) if (p.playerId === playerId) out.set(p.gameId, p.side)
  for (const q of queue) if (q.playerId === playerId) out.set(q.gameId, q.side)
  return out
}

/**
 * The sent picks the server has NOT yet confirmed.
 *
 * Anything it now agrees with can be forgotten; everything else is still
 * covering a gap and has to be kept.
 */
export function confirmed(
  sent: readonly Pending[],
  serverPicks: readonly { gameId: string; playerId: string; side: Side }[],
  playerId: string,
): Pending[] {
  const server = new Map(
    serverPicks.filter((p) => p.playerId === playerId).map((p) => [p.gameId, p.side]),
  )
  return sent.filter((p) => p.playerId !== playerId || server.get(p.gameId) !== p.side)
}

/** The browser's storage, or a stub where there isn't one. */
export function browserStore(): Store {
  try {
    if (typeof localStorage !== 'undefined') return localStorage
  } catch {
    /* Blocked by settings; fall through. */
  }
  return { getItem: () => null, setItem: () => {} }
}

/**
 * Whether a failed pick should be given up on rather than retried.
 *
 * A 409 is "picks are closed for this week", a 403 is "that player has a PIN",
 * a 404 is "no such game". None of them will read differently in four seconds,
 * and an item that stays in the queue goes on being DRAWN as the player's
 * pick - so a tap that landed a minute after the deadline looked saved for as
 * long as the app stayed open.
 *
 * 429 is excluded on purpose: being asked to slow down is not a refusal, and
 * dropping the pick would be the one outcome the queue exists to prevent.
 */
export function permanent(status: number): boolean {
  return status >= 400 && status < 500 && status !== 429 && status !== 408
}
